/**
 * Pulsante per mettere in watchlist uno strumento già noto al sistema.
 *
 * Usa `instrumentId` e non ISIN o simbolo: lo strumento esiste già a catalogo
 * (è in portafoglio), quindi passare l'identificatore evita che il backend
 * debba risolverlo di nuovo e, soprattutto, che ne crei un duplicato quando
 * l'anagrafica ha solo uno dei due codici.
 */

import { useMemo } from 'react';
import { ApiError } from '@/lib/api-client';
import { Button } from '@/ui';
import { useAddWatchlistItem, useWatchlist } from './api';

type Props = {
  instrumentId: string;
  instrumentName: string;
  /** Variante compatta per le celle di tabella. */
  compact?: boolean;
};

export function WatchlistToggleButton({ instrumentId, instrumentName, compact = false }: Props) {
  const watchlistQuery = useWatchlist();
  const addItem = useAddWatchlistItem();

  const alreadyWatched = useMemo(
    () => (watchlistQuery.data?.items ?? []).some((item) => item.instrument.id === instrumentId),
    [watchlistQuery.data, instrumentId],
  );

  // Finché la watchlist non è caricata non si può sapere se il titolo c'è già:
  // mostrare "Aggiungi" e poi correggersi sarebbe peggio di attendere.
  if (watchlistQuery.isPending) {
    return (
      <Button variant="ghost" disabled>
        …
      </Button>
    );
  }

  if (alreadyWatched) {
    return (
      <span className="watchlist-toggle__done" title={`${instrumentName} è già in watchlist`}>
        ★ In watchlist
      </span>
    );
  }

  const failed = addItem.isError ? addItem.error : null;
  const message =
    failed instanceof ApiError ? failed.message : failed ? 'Errore imprevisto' : null;

  return (
    <span className="watchlist-toggle">
      <Button
        variant={compact ? 'ghost' : 'secondary'}
        loading={addItem.isPending}
        onClick={() => addItem.mutate({ instrumentId })}
        title={`Aggiungi ${instrumentName} alla watchlist`}
      >
        ☆ {compact ? 'Watchlist' : 'Aggiungi a watchlist'}
      </Button>
      {message && (
        <span className="watchlist-toggle__error" role="alert">
          {message}
        </span>
      )}
    </span>
  );
}
